import React from 'react';
import { CheckCircle, WarningCircle } from 'phosphor-react';
import usePresence, { useUltimoValor } from '../../use/usePresence';

interface AvisoProps {
  /** Texto do aviso. Vazio/nulo esconde — e é o que dispara a saída. */
  mensagem?: string | null;
  tipo?: 'erro' | 'sucesso';
  className?: string;
}

/**
 * A caixinha de erro (ou de sucesso) que ficava embaixo dos formulários.
 *
 * Existiam 15 cópias quase iguais: 7 com `animate-slide-down`, 8 sem nenhuma
 * animação, **nenhuma** com saída, e 8 delas sem um único `dark:` — no tema
 * escuro viravam uma caixa clara. Aqui a cor é definida uma vez.
 *
 * O `useUltimoValor` segura o texto durante a saída: sem ele a mensagem some no
 * exato frame em que a animação deveria começar, e a caixa sai vazia.
 */
const CORES: Record<string, string> = {
  erro: 'bg-red-50 dark:bg-red-900/20 border-red-100 dark:border-red-800 text-red-600 dark:text-red-400',
  sucesso: 'bg-sage-50 dark:bg-sage-900/20 border-sage-200 dark:border-sage-800 text-sage-700 dark:text-sage-300',
};

export default function Aviso({ mensagem, tipo = 'erro', className = '' }: AvisoProps) {
  const aberto = Boolean(mensagem);
  const { montado, estado } = usePresence(aberto, 180);
  const texto = useUltimoValor(mensagem || null);

  if (!montado) return null;

  const Icone = tipo === 'erro' ? WarningCircle : CheckCircle;

  return (
    <div
      role={tipo === 'erro' ? 'alert' : 'status'}
      data-state={estado}
      className={`
        flex items-center gap-2 px-3 py-2.5 border rounded-xl
        data-[state=open]:animate-slide-down data-[state=closed]:animate-exit
        ${CORES[tipo]} ${className}
      `}
    >
      <Icone className="w-4 h-4 flex-shrink-0" weight="fill" aria-hidden="true" />
      <p className="text-xs">{texto}</p>
    </div>
  );
}
